import { queryOptions } from '@tanstack/react-query'
import { merklApiClient } from '../api-client'
import { Address, formatUnits } from 'viem'
import { NormalizedUnitNumber } from '@/domain/types/NumericValues'
import { formatPointsAmount } from '../utils'
import { epochToTimestampMap, POINTS_TOKEN_ADDRESS } from '@/config/chain/points'

const formatter = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  maximumFractionDigits: 1,
})

export function userPointsQueryOptions({ account, chainId }: { account: Address; chainId: number }) {
  return queryOptions({
    queryKey: userPointsQueryKey(account),
    queryFn: async () => {
      const userRewardsResponse = await merklApiClient.getUserRewards({
        chainId,
        test: true,
      })

      return {
        userRewards: userRewardsResponse.rewards,
      }
    },
    select: ({ userRewards }) => {
      const pointsToken = userRewards?.find(
        (reward) => reward.token.address.toLowerCase() === POINTS_TOKEN_ADDRESS.toLowerCase(),
      )

      const finishedWeeksPoints = epochToTimestampMap.map(
        ({ epoch, timestamp, calculating: calculatingFromMap }, index) => {
          const breakdown = pointsToken?.breakdowns.find((breakdown) => breakdown.reason === epoch.toString())
          const calculating = index === 0 || calculatingFromMap
          const WEEK_IN_SECONDS = 604800

          const startTimestamp = timestamp
          const endTimestamp = calculating
            ? startTimestamp + WEEK_IN_SECONDS
            : epochToTimestampMap.find((item) => item.epoch === epoch + 1)?.timestamp

          const dateFormatOptions: Intl.DateTimeFormatOptions = {
            year: 'numeric',
            month: 'short',
            day: '2-digit',
            timeZone: 'UTC',
          }

          const formattedStartTimestamp = startTimestamp
            ? new Date(startTimestamp * 1000).toLocaleDateString('en-US', dateFormatOptions)
            : undefined

          const formattedEndTimestamp = endTimestamp
            ? new Date(endTimestamp * 1000 - 1).toLocaleDateString('en-US', dateFormatOptions)
            : undefined

          if (!breakdown) {
            return {
              epochNumber: epoch,
              formattedStartTimestamp,
              formattedEndTimestamp,
              calculating,
              amount: '0',
              userPointsCalculated: NormalizedUnitNumber(0),
              weeklySum: NormalizedUnitNumber(0),
            }
          }

          const pointsAmount = typeof breakdown.amount === 'bigint' ? breakdown.amount : BigInt(breakdown.amount ?? 0)
          const pendingAmount =
            typeof breakdown.pending === 'bigint' ? breakdown.pending : BigInt(breakdown.pending ?? 0)

          const totalAmount = pointsAmount + pendingAmount
          const userPointsCalculated = NormalizedUnitNumber(formatUnits(totalAmount, 18))

          return {
            epochNumber: epoch,
            formattedStartTimestamp,
            formattedEndTimestamp,
            calculating,
            amount:
              typeof breakdown.amount === 'bigint' ? breakdown.amount * 100n : Number(breakdown.amount ?? 0) * 100,
            userPointsCalculated: NormalizedUnitNumber(Number(userPointsCalculated) * 100),
            weeklySum: NormalizedUnitNumber(0),
          }
        },
      )

      if (!pointsToken || !pointsToken.breakdowns) {
        return {
          amount: NormalizedUnitNumber(0),
          formattedAmount: formatter.format(0),
          finishedWeeksPoints,
        }
      }

      let runningTotal = NormalizedUnitNumber(0)

      const formattedFinishedWeeksPoints = [...finishedWeeksPoints]

      for (let i = formattedFinishedWeeksPoints.length - 1; i >= 0; i--) {
        const week = formattedFinishedWeeksPoints[i]!
        runningTotal = NormalizedUnitNumber(runningTotal.plus(week.userPointsCalculated))

        formattedFinishedWeeksPoints[i] = {
          ...week,
          weeklySum: runningTotal,
        }
      }

      return {
        ...formatPointsAmount({
          amount: String(pointsToken.amount || '0'),
          pending: String(pointsToken.pending || '0'),
          decimals: pointsToken.token.decimals,
        }),
        finishedWeeksPoints: formattedFinishedWeeksPoints,
      }
    },
    enabled: !!account,
  })
}

export function userPointsQueryKey(account: Address): unknown[] {
  return ['merkl', 'user-points', account]
}
